/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/utils/proactiveTokenRefresh.ts
 * 版本: 1.0
 * 创建日期: 2026-05-19
 * 创建人: AI Assistant
 *
 * 主动 Token 续期 — 基于 visibilitychange 事件
 *
 * 解决问题：用户长时间空闲后切回页面，access_token 已过期，
 * 被动 401 刷新导致首次请求延迟（需等待 refresh 完成+重试）。
 * 此模块在页面变为可见时主动静默续期，使后续 API 调用无感。
 */

import axios from 'axios'
import { TokenManager } from '../api/http'

/** JWT payload 中至少包含 exp 字段 */
interface JwtPayload {
  exp: number
  [key: string]: unknown
}

/** 解析 JWT payload（不验证签名，仅读取 claims） */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded) as JwtPayload
  } catch {
    return null
  }
}

/** 判断 token 是否将在 bufferSeconds 内过期 */
function isTokenExpiringSoon(token: string, bufferSeconds: number = 120): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload?.exp) return true
  const nowSec = Math.floor(Date.now() / 1000)
  return payload.exp - nowSec <= bufferSeconds
}

let installed = false
let lastRefreshAt = 0
const MIN_REFRESH_INTERVAL_MS = 10_000

/**
 * 安装主动 Token 续期监听器。
 *
 * 输入: baseURL — API 基础路径（如 '/api/v1/'），用于拼接 refresh 端点
 * 输出: cleanup 函数，调用后移除监听
 *
 * 行为：
 * - 页面从 hidden→visible 时检测 access_token 是否即将过期（2分钟缓冲）
 * - 如过期则静默使用 refresh_token 续期
 * - 续期失败不弹窗，交由后续请求的 401 拦截器处理
 */
export function installProactiveTokenRefresh(baseURL: string = '/api/v1/'): () => void {
  if (installed) return () => {}
  if (typeof document === 'undefined') return () => {}

  const handler = async () => {
    if (document.visibilityState !== 'visible') return

    const accessToken = TokenManager.getAccessToken()
    if (!accessToken) return

    if (!isTokenExpiringSoon(accessToken, 120)) return

    const now = Date.now()
    if (now - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) return
    lastRefreshAt = now

    const refreshToken = TokenManager.getRefreshToken()
    if (!refreshToken) return

    try {
      const normalizedBase = baseURL.endsWith('/') ? baseURL : `${baseURL}/`
      const refreshUrl = `${normalizedBase}auth/token/refresh/`
      const response = await axios.post<{ access: string; refresh?: string }>(
        refreshUrl,
        { refresh: refreshToken }
      )
      const { access, refresh } = response.data
      if (access && refresh) {
        TokenManager.setTokens(access, refresh)
      } else if (access) {
        TokenManager.setAccessToken(access)
      }
    } catch {
      // 静默失败：交由后续请求的 401 拦截器统一处理
    }
  }

  document.addEventListener('visibilitychange', handler)
  installed = true

  return () => {
    document.removeEventListener('visibilitychange', handler)
    installed = false
  }
}
